import { ref, computed, onUnmounted } from 'vue';

/**
 * useVenueMap
 *
 * Composable for rendering an SVG venue map and managing zone interactions.
 * Agnostic of invitations or any specific business flow — purely ticketing/front domain.
 *
 * The SVG is expected to be injected via v-html in the consumer component.
 * After the DOM update, the consumer must call `attachMapListeners(containerEl)` to wire up
 * availability classes, selection highlight and click/hover events.
 *
 * Simplification vs legacy SvgComponent:
 *   - No network fetch inside the composable. The raw SVG string is provided externally
 *     (from a prop or a store). This keeps the composable pure and testable.
 *   - No implicit Vuex dependency. All zone data is passed in as a reactive ref or plain array.
 *
 * @param {Object}             options
 * @param {Ref<Array>}         options.zones            - Reactive array of zone objects { id, esta_disponible?, available? }.
 *                                                        Availability is read from `available` (normalised) OR `esta_disponible` (legacy/raw API).
 *                                                        Either field is enough; they can coexist.
 * @param {Ref<string|number>} options.selectedZoneId   - Currently selected zone id (controlled from parent).
 * @param {string}             [options.zoneClass]      - CSS class on SVG elements that represent zones (default: 'recinto-zona').
 * @param {string}             [options.dataAttr]       - data-* attribute holding the zone id (default: 'region').
 * @param {Function}           [options.onZoneClick]    - Callback(zone) when user clicks an available zone.
 * @param {Function}           [options.onZoneHover]    - Callback(zoneId:number, entering:boolean) on mouseenter/leave.
 */
export function useVenueMap({
    zones = ref([]),
    selectedZoneId = ref(null),
    zoneClass = 'recinto-zona',
    dataAttr = 'region',
    onZoneClick = null,
    onZoneHover = null,
} = {}) {
    // ─── State ──────────────────────────────────────────────────────────────
    /** True once attachMapListeners() has completed successfully at least once */
    const isReady = ref(false);

    /**
     * Map of { [zoneId]: colorString } reflecting the REAL fill extracted from the
     * SVG nodes after the map is attached. Used to sync the list colour with what is
     * actually visible in the SVG, regardless of what the API payload says.
     */
    const zoneColors = ref({});

    // Normalised zone lookup: { [id]: zone }
    const zoneMap = computed(() => {
        const map = {};
        const list = zones.value ?? zones;
        for (const z of list) {
            map[String(z.id)] = z;
        }
        return map;
    });

    // ─── DOM listener tracking ───────────────────────────────────────────────
    /** Registered listeners stored for cleanup */
    const _listeners = [];

    function _removeListeners() {
        for (const { el, type, fn } of _listeners) {
            el.removeEventListener(type, fn);
        }
        _listeners.length = 0;
    }

    // ─── Availability + selection helpers ───────────────────────────────────

    /**
     * Extract the real fill colour from an SVG zone element.
     * Priority: inline style fill > fill attribute.
     * Returns null if no meaningful colour is found.
     * NOTE: getComputedStyle is intentionally skipped — it resolves the CSS
     * cascade (including the global --altcolor branding variable) and would
     * return the site theme colour instead of the zone's actual SVG fill.
     */
    function _readSvgFill(el) {
        // Only read the fill that was authored in the SVG itself (inline style or
        // presentation attribute).  getComputedStyle is intentionally NOT used here
        // because it resolves CSS cascade — including the global --altcolor branding
        // variable — which contaminates zoneColors with the site theme colour instead
        // of the real zone colour.
        const inline = el.style.fill;
        if (inline && inline !== 'none' && inline !== '') return inline;
        const attr = el.getAttribute('fill');
        if (attr && attr !== 'none' && attr !== '') return attr;
        return null;
    }


    /**
     * Returns true if the zone object marks the zone as available.
     * Supports both legacy `esta_disponible` (raw API / legacy components) and
     * normalised `available` (service-layer normalisation in invitaciones.js et al.).
     * Both fields may coexist; either truthy value is enough.
     */
    function _isAvailable(zone) {
        if (!zone) return false;
        return Boolean(zone.available ?? zone.esta_disponible);
    }

    function _applyAvailability(el, zone) {
        if (_isAvailable(zone)) {
            el.classList.remove('no-disponible');
            // CSS (.venue-map .recinto-zona:not(.no-disponible) *) fuerza opacity:1
            // y fill-opacity:1 en raíz + hijos, así que ya no necesitamos _clearOpacity.
            // Solo queda sanitizar el valor del fill cuando lleva alpha embebido
            // (rgba() / #rrggbbaa), que fill-opacity no cubre.
            const rawFill = _readSvgFill(el);
            if (rawFill) {
                const opaqueFill = _toOpaque(rawFill);
                if (opaqueFill !== rawFill) {
                    el.setAttribute('fill', opaqueFill);
                    if (el.style.fill) el.style.fill = opaqueFill;
                }
            }
            for (const child of el.querySelectorAll('[fill],[style*="fill"]')) {
                const childFill = _readSvgFill(child);
                if (childFill) {
                    const childOpaque = _toOpaque(childFill);
                    if (childOpaque !== childFill) {
                        child.setAttribute('fill', childOpaque);
                        if (child.style.fill) child.style.fill = childOpaque;
                    }
                }
            }
        } else {
            el.classList.add('no-disponible');
        }
    }

    /**
     * Make a color fully opaque.
     * Handles: hex (#rgb, #rrggbb, #rrggbbaa), rgb(), rgba().
     * Returns the original string if not recognised.
     */
    function _toOpaque(color) {
        if (!color) return color;
        const trimmed = color.trim();

        // rgba(r,g,b,a) → rgb(r,g,b)
        const rgbaMatch = trimmed.match(/^rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*[\d.]+\s*\)$/i);
        if (rgbaMatch) return `rgb(${rgbaMatch[1]},${rgbaMatch[2]},${rgbaMatch[3]})`;

        // #rrggbbaa → #rrggbb
        if (/^#[0-9a-f]{8}$/i.test(trimmed)) return trimmed.slice(0, 7);

        // #rgba → #rgb (4-char short hex with alpha)
        if (/^#[0-9a-f]{4}$/i.test(trimmed)) return trimmed.slice(0, 4);

        return trimmed;
    }

    function _applySelectedStyle(el, isSelected) {
        if (isSelected) {
            // Capture the current fill from the SVG attribute or computed style
            const existingFill = el.getAttribute('fill') || el.style.fill || '';
            // Store original fill for later restoration (opacity is never restored —
            // we always clear it on deselect to prevent alpha bleed from SVG authoring).
            if (!el.dataset.originalFill) {
                el.dataset.originalFill = existingFill || '__none__';
            }
            const opaqueFill = _toOpaque(existingFill) || '#1e3a5f';
            el.style.fill = opaqueFill;
            el.style.opacity = '1';
            el.style.stroke = 'none';

            // Make inner text elements white
            const textEls = el.querySelectorAll('text, tspan');
            for (const t of textEls) {
                if (!t.dataset.originalFill) {
                    t.dataset.originalFill = t.getAttribute('fill') || t.style.fill || '__none__';
                }
                t.style.fill = '#ffffff';
            }
        } else {
            // Restore original fill
            if (el.dataset.originalFill) {
                el.style.fill = el.dataset.originalFill === '__none__' ? '' : el.dataset.originalFill;
                delete el.dataset.originalFill;
            }
            // CSS (.venue-map .recinto-zona:not(.no-disponible) *) fuerza
            // opacity:1 y fill-opacity:1 — no es necesario limpiar atributos.
            el.style.stroke = '';

            // Restore inner text fills
            const textEls = el.querySelectorAll('text, tspan');
            for (const t of textEls) {
                if (t.dataset.originalFill) {
                    t.style.fill = t.dataset.originalFill === '__none__' ? '' : t.dataset.originalFill;
                    delete t.dataset.originalFill;
                }
            }
        }
        el.classList.toggle('venue-map__zone--selected', isSelected);
    }

    function _applySelectedClass(el, regionId) {
        const isSelected = String(regionId) === String(selectedZoneId.value ?? '');
        _applySelectedStyle(el, isSelected);
    }

    function _handleRegionClick(regionId) {
        const zone = zoneMap.value[String(regionId)];
        if (!_isAvailable(zone)) return;
        if (onZoneClick) onZoneClick(zone);
    }

    // ─── Public API: map wiring ──────────────────────────────────────────────

    /**
     * Must be called after the SVG is rendered in the DOM (e.g. after nextTick following
     * a v-html update). Wires availability classes, selected highlight and click/hover events.
     *
     * @param {Element} containerEl - DOM element that hosts the rendered SVG (v-html host).
     */
    function attachMapListeners(containerEl) {
        if (!containerEl) return;

        _removeListeners();
        isReady.value = false;

        const regions = containerEl.getElementsByClassName(zoneClass);
        const colors = {};

        for (const el of regions) {
            if (!el.dataset) continue;

            const regionId = el.dataset[dataAttr];
            if (!regionId) continue;

            const zone = zoneMap.value[String(regionId)];

            // Capture real SVG fill before any style mutations.
            // Force fully opaque so that zoneColors always provides solid colours
            // to consumers (e.g. ZoneTable colour dots), even when the SVG source
            // encodes alpha via rgba() or #rrggbbaa.
            const svgFill = _readSvgFill(el);
            if (svgFill) colors[String(regionId)] = _toOpaque(svgFill);

            _applyAvailability(el, zone);
            _applySelectedClass(el, regionId);

            // Hover events
            if (onZoneHover) {
                const enterFn = e => onZoneHover(Number(regionId), true, e);
                const leaveFn = e => onZoneHover(Number(regionId), false, e);
                el.addEventListener('mouseenter', enterFn);
                el.addEventListener('mouseleave', leaveFn);
                _listeners.push(
                    { el, type: 'mouseenter', fn: enterFn },
                    { el, type: 'mouseleave', fn: leaveFn },
                );
            }

            // Click event
            const clickFn = () => _handleRegionClick(regionId);
            el.addEventListener('click', clickFn);
            _listeners.push({ el, type: 'click', fn: clickFn });
        }

        zoneColors.value = colors;
        isReady.value = true;
    }

    /**
     * Re-sync the CSS selected class without re-attaching all listeners.
     * Call when selectedZoneId changes without a full SVG re-render.
     *
     * @param {Element} containerEl
     */
    function syncSelectedZone(containerEl) {
        if (!containerEl) return;
        const regions = containerEl.getElementsByClassName(zoneClass);
        for (const el of regions) {
            const regionId = el.dataset?.[dataAttr];
            if (regionId) _applySelectedClass(el, regionId);
        }
    }

    /**
     * Sync hover highlight triggered from an external source (e.g. a ZoneTable row hover).
     * Sets --altcolor on the element to the real SVG fill so the highlighted-svg CSS rule
     * uses the exact same colour that is visible in the map (not a generic fallback).
     *
     * @param {Element}       containerEl
     * @param {string|number} newId   - Zone id to highlight (null to clear)
     * @param {string|number} oldId   - Previous zone id to un-highlight
     */
    function syncHoveredZone(containerEl, newId, oldId) {
        if (!containerEl) return;
        if (oldId != null) {
            const el = containerEl.querySelector(`[data-${dataAttr}="${oldId}"]`);
            if (el) {
                el.classList.remove('highlighted-svg');
                el.style.removeProperty('--altcolor');
            }
        }
        if (newId != null) {
            const el = containerEl.querySelector(`[data-${dataAttr}="${newId}"]`);
            if (el) {
                const color = zoneColors.value[String(newId)];
                if (color) el.style.setProperty('--altcolor', color);
                el.classList.add('highlighted-svg');
            }
        }
    }

    // ─── Cleanup ─────────────────────────────────────────────────────────────
    onUnmounted(_removeListeners);

    // ─── Public API ──────────────────────────────────────────────────────────
    return {
        /** True once attachMapListeners() has completed at least once */
        isReady,
        /**
         * Real SVG fill colours per zone id, extracted during attachMapListeners().
         * Use these to synchronise list colours with what is visually shown in the SVG.
         * Shape: { [zoneId: string]: string }
         */
        zoneColors,
        /**
         * Wire availability classes, selected highlight and click/hover events to the
         * SVG elements inside containerEl. Must be called after v-html update (nextTick).
         */
        attachMapListeners,
        /** Re-sync the selected-zone CSS class without re-attaching all listeners */
        syncSelectedZone,
        /** Reflect external hover (e.g. from ZoneTable) onto the SVG map */
        syncHoveredZone,
        /** Remove all DOM listeners (called automatically on unmount) */
        destroy: _removeListeners,
    };
}
